import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Card, CardBody, CardHeader, Col, Collapse, Row } from 'reactstrap';
import dayjs from 'dayjs';

import { AppointmentStatuses } from '~constants/maps';

import { dateInputFieldFormat, weeklyPlannerDayDisplayFormat } from '~libs/dayjs';

import { Button } from '~components';
import Icon, { faChevronDown, faChevronUp } from '~components/icon';
import ModalCard from '~components/ModalCard';
import Spinner from '~components/Spinner';
import StatusStrip from '~weekly-planner/components/card/StatusStrip';

import { acceptAll, acknowledge, emailAll, getAll } from '~appointments/actions/appointments';

import { selectAppointments } from '~appointments/selectors/appointments';

const UpcomingAppointments: React.FC = () => {
  const dispatch = useAppDispatch();
  const { ACCEPTED, PENDING_CARER_APPROVAL } = AppointmentStatuses;

  const [loading, setLoading] = useState<boolean>(false);
  const [openTarget, setTarget] = useState<string>('');
  const toggle = (id: string) => {
    setTarget(openTarget === id ? '' : id);
  };

  const appointments = useAppSelector(selectAppointments);
  const count = appointments.length;
  const week_start = dayjs().format(dateInputFieldFormat);

  const load = () => dispatch(getAll({ repeat: false, limit: 0, week_start }));

  const [isOpen, setOpen] = useState<boolean>(true);

  const acceptAppointment = async (id: number) => {
    setLoading(true);
    const { payload } = await dispatch(acknowledge({ id, status_id: ACCEPTED }));
    const { success } = payload;
    if (success) {
      setLoading(false);
      load();
    }
  };

  const handleAppointmentsAction = async (action: any, params: { [key: string]: any }) => {
    setLoading(true);
    await dispatch(action(params));
    setLoading(false);
  };

  const acceptAllAppointments = async () => {
    await handleAppointmentsAction(acceptAll, { week_start });
    load();
  };

  const emailAllAppointments = async () => {
    await handleAppointmentsAction(emailAll, { week_start });
  };

  useEffect(() => {
    load();
  }, []);

  const renderAcceptSection = (status: number, apptId: number) => {
    switch (status) {
      case PENDING_CARER_APPROVAL: {
        return (
          <>
            <span className="section-title">Pending Accept</span>
            <p>This appointment is ready to be accepted.</p>
            <Button onClick={() => acceptAppointment(apptId)} disabled={loading}>
              {loading ? <Spinner size={15} loading={loading} /> : 'Accept'}
            </Button>
            <small>To reject this appointment, call the office.</small>
          </>
        );
      }
      case ACCEPTED: {
        return (
          <>
            <span className="section-title">Appointment Accepted</span>
            <p>This appointment has been accepted.</p>
            <small>To change anything for this appointment, call the office.</small>
          </>
        );
      }
    }
  };

  const renderRow = (title: string, value: string, nextLine?: boolean) =>
    nextLine ? (
      <div className="row">
        <div className="row">
          <Col xs={4} className="fw-bold">
            {title}:
          </Col>
        </div>
        <div className="row">
          <div className="col">{value}</div>
        </div>
      </div>
    ) : (
      <div className="row">
        <Col xs={4} className="fw-bold">
          {title}:
        </Col>
        <div className="col">{value}</div>
      </div>
    );

  const renderAppointmentCards = () =>
    appointments.map(({ id, client, status, start_time, end_time, date, service_type, notes }) => {
      const isOpen = openTarget === id.toString();
      const displayDate = dayjs(date).format(weeklyPlannerDayDisplayFormat);

      const rowFields = [
        { label: 'Client', value: client.full_name },
        { label: 'Date', value: displayDate },
        { label: 'Times', value: `${start_time} - ${end_time}` },
        { label: 'Status', value: status.name },
        { label: 'Service Type', value: service_type.name },
        { label: 'Referrer', value: client.referrer_name },
        { label: 'Age', value: client.age },
        { label: 'Phone', value: client.phone },
        { label: 'Address', value: client.address },
        { label: 'Suburb', value: client.suburb },
        { label: 'Notes', value: notes, nextLine: true },
      ];

      return (
        <div key={`appt-card-${id}`} className="appointment-card">
          <Card>
            <CardHeader type="button" onClick={() => toggle(id.toString())} className="card-header">
              <StatusStrip status={status} />
              <div className="header-info">
                <Row>
                  <Col className="header-title">{client.full_name}</Col>
                  <Col xs={4} className="text-end">
                    <span className="fw-bold status">{status.name}</span>
                    <Icon icon={isOpen ? faChevronUp : faChevronDown} className="ms-2" />
                  </Col>
                </Row>
                <Row>
                  {displayDate}, {start_time} - {end_time}
                </Row>
              </div>
            </CardHeader>
            <Collapse isOpen={isOpen}>
              <CardBody className="card-body">
                <Row>
                  <span className="section-title">Appointment Details</span>
                  {rowFields.map(({ label, value, nextLine }) => renderRow(label, value, nextLine))}
                </Row>
                <hr />
                <Row className="mt-3">
                  <div className="options">{renderAcceptSection(status.id, id as number)}</div>
                </Row>
              </CardBody>
            </Collapse>
          </Card>
        </div>
      );
    });

  return (
    <ModalCard
      header={`Upcoming Appointments (${count})`}
      className="dashboard-modal"
      headerClassName="header fw-bold"
      isOpen={isOpen}
      setIsOpen={() => setOpen(!isOpen)}
    >
      <div className="upcoming-appointments">
        {count > 0 ? <div className="body">{renderAppointmentCards()}</div> : <p>No Upcoming Appointments!</p>}
        {count > 0 && (
          <div className="all-upcoming-buttons">
            <Button onClick={acceptAllAppointments} disabled={count < 1 && loading}>
              Accept All
            </Button>
            <Button onClick={emailAllAppointments} disabled={count < 1 && loading}>
              Email All
            </Button>
          </div>
        )}
      </div>
    </ModalCard>
  );
};

export default UpcomingAppointments;
